import { Component, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { map, takeUntil } from 'rxjs/operators';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { DataStudioRedesignService } from 'app/shared/services/data-services/data-studio-redesign/data-studio-redesign.service';
import { WarningsService } from 'app/data-studio-redesign/services/warnings.service';
import { DataStudioHelperService } from 'app/data-studio-redesign/services/data-studio-helper.service';
import { DataStudioQueryType, Stream } from 'app/shared/services/data-services/graphql';
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import {SignalSelectionCacher} from '../signal-selection-cacher/signal-selection-cacher';
import { DateTimeFormatService } from 'app/shared/services/date-time-format/date-time-format.service';
import { addSignal, addSignalWithTurbineType } from 'app/data-studio-redesign/helper/data-studio-signal.helper';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { defineLocale} from 'ngx-bootstrap/chronos';
import * as locale from 'ngx-bootstrap/locale';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { GRANULARITIES, GranularityType } from 'app/data-studio-redesign/services/data-analysis-helper';
import { LanguageService } from 'app/shared/services/language/language.service';

@Component({
    selector: 'app-data-scada',
    templateUrl: './data-scada.component.html',
    styleUrls: ['./data-scada.component.scss'],
    standalone: false
})
export class DataScadaComponent implements OnInit, OnChanges, OnDestroy {

  public bsConfig;
  public bsRangeValue: Date[];
  public minDate = new Date('01-01-2019');
  public maxDate = new Date();
  public theme$: Observable<any>;
  public selectedAggregation = 'park';
  public dataSelectOptions: any = {};
  public dataParks: Array<any>;
  public parkSelected;
  public turbinesSelected = [];
  public turbineTypes: Array<any> = [];
  public turbineType;
  public turbineTypeByPark: Array<any> = [];
  public turbineWithCodeArray: Array<any> = [];
  public assetParkTurbinesSelected: Array<any> = [];
  public assetParks: Array<any> = [];
  public assetParksNames: Array<string>;
  public parkNameSelected;
  public signalGroupArray: Array<any> = [];
  public totalSignals = 0;
  public totalSignalsSelected = 0;
  public selectAllSignalsFlag = false;
  public ambientSignalGroupSelected = 0;
  public electricalSignalGroupSelected = 0;
  public technicalSignalGroupSelected = 0;
  public temperatureSignalGroupSelected = 0;
  public turbineSelection: string;
  public turbinesList: string[];
  private enumStream = Stream;
  private enumQueryType = DataStudioQueryType;
  private originalTurbineSignals = [];
  private unsubscriber = new ReplaySubject();

  public granularityOptions: { values: GranularityType[], titles: string[] } = {
    values: [ ...Object.values(GRANULARITIES) ],
    titles: [
      'DATA_STUDIO.DATA_PARAMETERS.TEN_MINUTES',
      'DATA_STUDIO.DATA_PARAMETERS.ONE_HOUR',
      'DATA_STUDIO.DATA_PARAMETERS.ONE_DAY',
      'DATA_STUDIO.DATA_PARAMETERS.ONE_MONTH',
      'DATA_STUDIO.DATA_PARAMETERS.ONE_YEAR'
    ]
  };

  public aggregationOptions: { values: string[], titles: string[] } = {
    values: ['park', 'turbine_type', 'turbine'],
    titles: ['COMMON.PARK', 'DATA_STUDIO.DATA_PARAMETERS.TURBINE_TYPE', 'COMMON.TURBINE']
  };

  private unsubscribe$ = new Subject();
  public wrongDate = false;
  public turbineTypeSelection;
  public selectedGranularity: string;
  public selectedAggregationName: string;
  public dataParksNames: string[];

  constructor(private dataStudioService: DataStudioRedesignService,
              private warningService: WarningsService,
              private dataStudioHelper: DataStudioHelperService,
              private themeService: ThemeService,
              private selectedSignalsCacher: SignalSelectionCacher,
              private translateService: TranslateService,
              private localeService: BsLocaleService,
							private dateTimeFormatService: DateTimeFormatService,
              private languageService: LanguageService) {

    this.bsRangeValue = [new Date(), new Date()];
    this.selectedGranularity = this.granularityOptions.titles[0];
    this.selectedAggregationName = this.aggregationOptions.titles[0];
    this.initSelectOptions();
  }

  ngOnInit(): void {
    this.subscribeLanguageChange();
    this.bsConfig = this.themeService.getTheme().pipe(
      map((theme: EThemeName) => Object.assign({}, { containerClass: theme, rangeInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase(), dateInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase() }))
    );
    this.getAllParkAndTurbines();
  }

	ngOnChanges(changes): void {
    this.bsConfig = this.themeService.getTheme().pipe(
      map((theme: EThemeName) => Object.assign({}, { containerClass: theme, rangeInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase(), dateInputFormat: this.dateTimeFormatService.getDateFormat('dmy').toLocaleUpperCase() }))
    );
	}


  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.unsubscribe();
  }

  public changeDateRange(event) {
    if (event) {
      this.getDates(event);
      if (this.dataSelectOptions.fileRequest.signals !== undefined || this.dataSelectOptions.fileRequest.turbines !== undefined) {
        this.calculateSize();
      }
    }
  }

  private getDates(event) {
    const today = new Date();
    const dateA = new Date(event[0].getTime());
    this.dataSelectOptions.dateFrom = Number(dateA.setHours(0, 0, 0, 0));
    this.dataSelectOptions.fileRequest.startTS = this.dataSelectOptions.dateFrom;
    const dateB = new Date(event[1].getTime());
    const isToday =
      dateB.getDate() === today.getDate() &&
      dateB.getMonth() === today.getMonth() &&
      dateB.getFullYear() === today.getFullYear();
    this.dataSelectOptions.dateTo = isToday ? Number(dateB) : Number(dateB.setHours(23, 59, 59, 999));
    this.dataSelectOptions.fileRequest.endTS = this.dataSelectOptions.dateTo;
  }

  public selectGranularity(granularity) {
    this.selectedGranularity = granularity.option;
    this.dataSelectOptions.fileRequest.granularity = this.granularityOptions.values[granularity.index];
    this.dataSelectOptions.granularity = granularity.index;
    this.calculateSize();
  }

  public selectAggregation(event) {
    if (this.selectedAggregationName !== event.option) {
      this.selectedAggregationName = event.option
      this.selectedAggregation = this.aggregationOptions.values[event.index];
      this.parkSelected = null;
      this.parkNameSelected = null;
      this.turbineType = undefined;
      this.assetParkTurbinesSelected = [];
      switch (this.selectedAggregation) {
        case 'park':
          this.dataSelectOptions.aggregationLevel = '0';
          this.selectPark();
          break;
        case 'turbine':
          this.parkSelected = this.dataParks[0];
          this.turbineWithCodeArray = this.parkSelected.turbines.map(e => {
            return {
              ...e,
              displayName: `${this.parkSelected.fe_name} - ${e.code.replaceAll('_', ' ')}`,
              value: e.serialNav, parkCode: this.parkSelected.code, name: e.name, id: e._id, parkName: this.parkSelected.name, code: e.code
            };
          });
          this.dataSelectOptions.aggregationLevel = '1';
          this.selectedOneTurbine({option: this.turbineWithCodeArray[0].displayName});
          this.setTurbinesList();
          break;
        case 'turbine_type':
          this.dataSelectOptions.aggregationLevel = '2';
          this.selectTurbineType({option: this.turbineTypes[0]});
          break;
      }
    }
  }

  public selectTurbineType({option}) {
    this.turbineTypeSelection = option;
    this.turbinesSelected = [];
    this.getSignalsByTurbineType(option);
    this.turbineType = option;
    this.filterAssetsByTurbineType();
    this.dataSelectOptions.estimatedRows = 0;
    this.dataSelectOptions.estimatedFields = 0;
    this.dataSelectOptions.estimatedFileSize = 0;
    this.dataStudioService.sendTurbinesToPreview([]);
    this.calculateSize();
  }

  public selectPark(event = null) {
    this.turbinesSelected = [];
    this.parkSelected = event ? this.dataParks[event.index] : this.dataParks[0];
    if (this.selectedAggregation === 'turbine') {
      this.turbineWithCodeArray = this.parkSelected.turbines.map(e => {
        return {
          ...e,
          displayName: `${this.parkSelected.fe_name} - ${e.code.replaceAll('_', ' ')}`,
          value: e.serialNav, parkCode: this.parkSelected.code, name: e.name, id: e._id, parkName: this.parkSelected.name, code: e.code
        };
      });
      this.selectedOneTurbine({option: this.turbineWithCodeArray[0].displayName});
      this.setTurbinesList();
    } else {
      this.getAvailableTSForPark(this.parkSelected.name);
      this.getSignalsByParkName(this.parkSelected.name);
      this.parkSelected.turbines.sort(this.dataStudioHelper.naturalCompare);
      this.dataSelectOptions.assetPark = this.parkSelected.code;
      this.dataStudioService.sendTurbinesToPreview(this.parkSelected.turbines.sort(this.dataStudioHelper.naturalCompare));
    }
  }

  public selectedOneTurbine({option}) {
    const turbine = this.turbineWithCodeArray.find(item => item.displayName === option);
    if (turbine) {
      this.turbineSelection = turbine.displayName;
      this.getAvailableTSForPark(turbine.parkName);
      this.getSignalsByTurbineName(turbine.name);
      this.dataSelectOptions.assetPark = turbine.parkCode;
      this.dataSelectOptions.assetSelected = turbine.value;
      this.dataSelectOptions.fileRequest.turbines.push(turbine.id);
      this.dataStudioService.sendTurbinesToPreview([{...turbine, _id: turbine.id }]);
      this.calculateSize();
    }
  }

  public selectedAssetTurbines(event) {
    if (!this.turbinesSelected.find(data => data.parkCode === event.parkCode)) {
      this.turbinesSelected.push(event);
    } else {
      this.turbinesSelected[(this.turbinesSelected.findIndex(park => park.parkCode === event.parkCode))].turbines = event.turbines;
    }
    const onlyTurbines = [];
    this.turbinesSelected.forEach(element => {
      element.turbines.forEach(turbine => {
        onlyTurbines.push(turbine._id);
      });
    });
    this.dataSelectOptions.fileRequest.turbines = onlyTurbines;
    this.calculateSize();
    this.recalculateTurbineSignals(event);
  }

  public filterAssetsByTurbineType() {
    this.assetParkTurbinesSelected = [];
    const filteredArray: Array<any> = [];
    this.dataParks.forEach(park => {
      park.turbines.forEach(turbine => {
        if (turbine.type === this.turbineType && !filteredArray.includes(park)) {
          filteredArray.push(park);
        }
      });
    });
    this.assetParks = filteredArray;
    this.setAssetNames();
  }

  private getSignalsByTurbineType(turbineType: string) {
    this.selectedSignalsCacher.saveSelectedSignals(this.signalGroupArray);
    this.resetSignalArray();
    this.dataStudioService.getAllSignalsByTurbineType(turbineType, this.enumStream.S10m).subscribe(turbineSignals => {
      if (turbineSignals) {
        this.fillSignalArray(turbineSignals);
        this.selectedSignalsCacher.restoreSelectedSignals(this.signalGroupArray);
        this.checkSelectedSignals();
      }
    });
  }

  private getSignalsByParkName(parkName: string) {
    this.selectedSignalsCacher.saveSelectedSignals(this.signalGroupArray);
    this.resetSignalArray();
    this.dataStudioService.getAllSignalsByParkName(parkName, this.enumStream.S10m).subscribe(parkSignals => {
      if (parkSignals) {
        this.originalTurbineSignals = parkSignals[0].turbines;
        this.fillSignalArrayWithTurbineType(parkSignals[0].turbines);
        this.fillSignalArray(parkSignals[0].turbines);
        this.selectedSignalsCacher.restoreSelectedSignals(this.signalGroupArray);
        this.checkSelectedSignals();
      }
    });
  }

  private getSignalsByTurbineName(turbineName: string) {
    this.selectedSignalsCacher.saveSelectedSignals(this.signalGroupArray);
    this.resetSignalArray();
    this.dataStudioService.getAllSignalsByTurbineName(turbineName, this.enumStream.S10m).subscribe(turbineSignals => {
      if (turbineSignals) {
        this.fillSignalArray(turbineSignals);
        this.selectedSignalsCacher.restoreSelectedSignals(this.signalGroupArray);
        this.checkSelectedSignals();
      }
    });
  }

  private insertCommonSignals() {
    /* Manually add Physical Health signal to the "Technical" group */
    if (!this.signalGroupArray[2].signals.map(s => s.value).includes('LPHD1_PhyHealth_stVal_avg')) {
      this.signalGroupArray[2].signals.push({
        checked: false,
        name: 'SIGNALS.' + 'LPHD1_PhyHealth_stVal_avg' + '.DISPLAY_NAME',
        value: 'LPHD1_PhyHealth_stVal_avg',
        turbineType: ['*'],
        unit: '-',
        description: 'SIGNALS.' + 'LPHD1_PhyHealth_stVal_avg' + '.DESCRIPTION'
      });
    }
    if (this.signalGroupArray[0].signals.map(s => s.value).includes('WNAC1_WdSpd_min_mag_f')) {
      return;
    }
    this.signalGroupArray[0].signals.push({
      checked: false,
      name: 'SIGNALS.' + 'WNAC1_WdSpd_max_mag_f' + '.DISPLAY_NAME',
      value: 'WNAC1_WdSpd_max_mag_f',
      turbineType: ['*'],
      unit: 'm/s',
      description: 'SIGNALS.' + 'WNAC1_WdSpd_max_mag_f' + '.DESCRIPTION'
    });
    this.signalGroupArray[0].signals.push({
      checked: false,
      name: 'SIGNALS.' + 'WNAC1_WdSpd_min_mag_f' + '.DISPLAY_NAME',
      value: 'WNAC1_WdSpd_min_mag_f',
      turbineType: ['*'],
      unit: 'm/s',
      description: 'SIGNALS.' + 'WNAC1_WdSpd_min_mag_f' + '.DESCRIPTION'
    });
    this.signalGroupArray[0].signals.push({
      checked: false,
      name: 'SIGNALS.' + 'WNAC1_WdSpd_sdv_mag_f' + '.DISPLAY_NAME',
      value: 'WNAC1_WdSpd_sdv_mag_f',
      turbineType: ['*'],
      unit: 'm/s',
      description: 'SIGNALS.' + 'WNAC1_WdSpd_sdv_mag_f' + '.DESCRIPTION'
    });

  }

  private recalculateTurbineSignals(event) {
    const turbinesSelected = event.turbines;

    this.fillSignalArray(this.originalTurbineSignals, turbinesSelected);
    this.fillSignalArrayWithTurbineType(this.originalTurbineSignals, turbinesSelected);
    this.checkSelectedSignals();
  }

  private fillSignalArray(arrayData: any, turbinesSelected?: any) {
    if (turbinesSelected) {
      arrayData = arrayData.filter(a => turbinesSelected.map(t => t.code).includes(a.code));
    }
    if (arrayData.length === 0) {
      return;
    }
    arrayData.forEach(turbine => {
      turbine.ambient.forEach(signal => {
        if (addSignal(this.signalGroupArray[0].signals, signal, ['avg', 'status', 'min', 'max', 'sdv'])) {
          this.totalSignals++;
        }
      });
      turbine.electrical.forEach(signal => {
        if (addSignal(this.signalGroupArray[1].signals, signal, ['avg', 'min', 'sdv', 'max'])) {
          this.totalSignals++;
        }
      });
      turbine.technical.forEach(signal => {
        if (addSignal(this.signalGroupArray[2].signals, signal, ['avg', 'status', 'min', 'max', 'sdv'])) {
          this.totalSignals++;
        }
      });
      turbine.temperature.forEach(signal => {
        if (addSignal(this.signalGroupArray[3].signals, signal, ['avg', 'min', 'max', 'sdv'])) {
          this.totalSignals++;
        }
      });
    });
    this.insertCommonSignals();
    this.signalGroupArray = this.sortTranslateSignals(this.signalGroupArray);
    this.dataStudioService.sendSignalsToPreview(this.signalGroupArray.flatMap(e => e.signals));
    this.totalSignals = this.signalGroupArray.flatMap(e => e.signals).length;
  }

  private fillSignalArrayWithTurbineType(arrayData: any, turbinesSelected?: any) {
    this.turbineTypeByPark = [];
    const signalAdded = (turbine) => {
      this.totalSignals++;
      if (!this.turbineTypeByPark.includes(turbine.type)) {
        this.turbineTypeByPark.push(turbine.type);
      }
    }
    if (turbinesSelected) {
      arrayData = arrayData.filter(a => turbinesSelected.map(t => t.code).includes(a.code));
    }
    arrayData.forEach(turbine => {
      turbine.ambient.forEach(signal => {
        if (addSignalWithTurbineType(this.signalGroupArray[0].signals, signal, turbine, ['avg'])) {
          signalAdded(turbine);
        }
      });
      turbine.electrical.forEach(signal => {
        if (addSignalWithTurbineType(this.signalGroupArray[1].signals, signal, turbine, ['avg'])) {
          signalAdded(turbine);
        }
      });
      turbine.technical.forEach(signal => {
        if (addSignalWithTurbineType(this.signalGroupArray[2].signals, signal, turbine, ['avg'])) {
          signalAdded(turbine);
        }
      });
      turbine.temperature.forEach(signal => {
        if (addSignalWithTurbineType(this.signalGroupArray[3].signals, signal, turbine, ['avg'])) {
          signalAdded(turbine);
        }
      });
    });
    if (this.turbineTypeByPark.length > 1) {
      this.fillSignalsTurbineType(arrayData);
    }
    this.dataStudioService.sendSignalsToPreview(this.signalGroupArray.flatMap(e => e.signals));
    this.totalSignals = this.signalGroupArray.flatMap(e => e.signals).length;
    this.signalGroupArray.forEach(sg => {
      sg.signals.sort((a, b) => this.sortTranslates(a, b));
    });
  }

  private fillSignalsTurbineType(arrayData: any) {
    const totalSignals = [];
    this.signalGroupArray.forEach(sg => {
      sg.signals.forEach(s => {
        totalSignals.push({name: s.value, turbineType: []});
      });
    });
    arrayData.forEach(turbine => {
      const signalGroups = this.signalGroupArray.map(sg => sg.signalGroupName.name);
      signalGroups.forEach(g => {
        turbine[g].forEach(a => {
          const index = totalSignals.map(s => s.name).indexOf(a.name);
          totalSignals.map(s => s.name).includes(a.name) ? totalSignals[index].turbineType.includes(turbine.type) ? null : totalSignals[index].turbineType.push(turbine.type) : null;
        });
      });
    });
    this.signalGroupArray.forEach(sg => {
      sg.signals.forEach(s => {
        const index = totalSignals.map(ts => ts.name).indexOf(s.value);
        s.turbineType = totalSignals[index].turbineType;
      });
    });
  }

  private resetSignalArray() {
    this.dataSelectOptions.fileRequest.signals = [];
    this.dataSelectOptions.fileRequest.turbines = [];
    this.totalSignalsSelected = 0;
    this.selectAllSignalsFlag = false;
    this.totalSignals = this.ambientSignalGroupSelected =
      this.electricalSignalGroupSelected = this.technicalSignalGroupSelected =
      this.temperatureSignalGroupSelected = 0;
    this.signalGroupArray = [];
    this.signalGroupArray.push({signalGroupName: {checked: false, name: 'ambient'}, signals: []});
    this.signalGroupArray.push({signalGroupName: {checked: false, name: 'electrical'}, signals: []});
    this.signalGroupArray.push({signalGroupName: {checked: false, name: 'technical'}, signals: []});
    this.signalGroupArray.push({signalGroupName: {checked: false, name: 'temperature'}, signals: []});
    this.totalSignals = this.signalGroupArray.flatMap(e => e.signals).length;
  }

  private getAllParkAndTurbines() {
    this.dataStudioService.getAllParkAndTurbines().subscribe(response => {
      if (response) {
        this.dataParks = JSON.parse(JSON.stringify(response));
        this.assetParks = JSON.parse(JSON.stringify(response));
        this.dataParks.sort((a, b) => {
          return a.fe_name.localeCompare(b.fe_name);
        });
        this.dataParksNames = this.dataParks.map(park => park.fe_name);
        this.assetParks.sort((a, b) => {
          return a.fe_name.localeCompare(b.fe_name);
        });
        this.setAssetNames();
        this.getTurbineTypes(this.dataParks);
        this.getTurbineWithCode(this.dataParks);
        this.initSelectOptions();
        this.selectPark();
      }
    });
  }

  private getTurbineTypes(array: Array<any>) {
    array.forEach(element => {
      element.turbines.forEach(turbine => {
        if (!this.turbineTypes.includes(turbine.type)) {
          this.turbineTypes.push(turbine.type);
        }
      });
    });
    this.turbineTypes.sort();
  }

  private getTurbineWithCode(array: Array<any>) {
    array.forEach(element => {
      element.turbines.sort(this.dataStudioHelper.naturalCompare);
      element.turbines.forEach(turbine => {
        this.turbineWithCodeArray.push({ displayName: element.name + ' - ' + turbine.code.replace('_', ' ').replace('_', ' '), value: turbine.serialNav, parkCode: element.code, name: turbine.name, id: turbine._id, parkName: element.name, code: turbine.code });
      });
    });
    this.setTurbinesList();
  }

  public addPark() {
    const selectedPark = this.assetParks.find(park => park.fe_name === this.parkNameSelected);
    this.getAvailableTSForPark(selectedPark.name);
    this.assetParkTurbinesSelected.push(selectedPark);
    this.assetParkTurbinesSelected.forEach(element => {
      element.turbines.sort(this.dataStudioHelper.naturalCompare);
    });
    this.assetParks = this.assetParks
        .filter((park) => park.fe_name !== this.parkNameSelected)
        .sort();
    this.setAssetNames();
    this.dataStudioService.sendTurbinesToPreview(this.assetParkTurbinesSelected.flatMap(e => e.turbines));
    this.parkNameSelected = null;
  }

  public selectParkAsset(event) {
    this.parkNameSelected = event.option;
  }

  public removeParkList(event) {
    this.assetParkTurbinesSelected = this.assetParkTurbinesSelected.filter(
        (park) => park.name !== event.parkName
    );
    this.assetParks.push(this.dataParks.find(park => park.name === event.parkName));
    this.assetParks.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
    this.setAssetNames();
    event.turbines.forEach(turbine => {
      for (let i = 0; i < this.dataSelectOptions.fileRequest.turbines.length; i++) {
        if (turbine._id === this.dataSelectOptions.fileRequest.turbines[i]) {
          this.dataSelectOptions.fileRequest.turbines.splice(i, 1);
        }
      }
    });
    this.dataStudioService.sendTurbinesToPreview(this.assetParkTurbinesSelected.flatMap(e => e.turbines));
    this.calculateSize();
  }

  public calculateSize() {
    const days = this.warningService.getDays(this.dataSelectOptions.dateFrom, this.dataSelectOptions.dateTo);
    const columns = 5 + (this.dataSelectOptions.fileRequest.signals.length);  // Right now 5 fields: Date UTC, Turbine Timezone date, User Timezone date, Turbine Code, Turbine Serial
    let rows = this.warningService.getRowsNumber(this.dataSelectOptions.granularity, days);
    let fields = this.warningService.getFieldsNumber(this.dataSelectOptions.granularity, columns, days);
    if (this.dataSelectOptions.aggregationLevel === '0' || this.dataSelectOptions.aggregationLevel === '2') { // ParkLevel  -> we multiply rows & fields for the number of turbines before getting the file size
        let turbines = 0;
        this.dataSelectOptions.fileRequest.turbines.forEach(element => {
          turbines++;
        });
        rows = rows * turbines;
        fields = fields * turbines;
        this.dataSelectOptions.estimatedRows = rows;
        this.dataSelectOptions.estimatedFields = fields;
        this.dataSelectOptions.estimatedFileSize = this.warningService.getFileSize(fields);
    } else {
      this.dataSelectOptions.estimatedRows = rows;
      this.dataSelectOptions.estimatedFields = fields;
      this.dataSelectOptions.estimatedFileSize = this.warningService.getFileSize(fields);
    }
    this.dataStudioHelper.setDateObservable$(this.dataSelectOptions);
  }

  private initSelectOptions() {
    this.dataSelectOptions.aggregationLevels = ['park', 'turbine', 'turbine type'];
    this.dataSelectOptions.aggregationLevel = '0';
    this.dataSelectOptions.assetSelected = null;
    this.dataSelectOptions.assetPark = null;
    this.dataSelectOptions.dateFrom = new Date().setHours(0, 0, 0, 0);
    this.dataSelectOptions.granularities = ['10 minutes', '1 hour', '1 day', '1 Month', '1 year'];
    this.dataSelectOptions.granularity = 0;
    this.dataSelectOptions.dateTo = this.dataSelectOptions.granularity !== '1 Month' && this.dataSelectOptions.granularity!=='1 year' ? new Date() : new Date().setHours(23, 59, 59, 999);
    this.dataSelectOptions.selectableColumns = [];
    this.dataSelectOptions.selectableColumnsNames = [];
    this.dataSelectOptions.selectableUnits = [];
    this.dataSelectOptions.selectedColumns = [];
    this.dataSelectOptions.estimatedRows = 0;
    this.dataSelectOptions.estimatedFields = 0;
    this.dataSelectOptions.estimatedFileSize = 0;
    this.dataSelectOptions.fileRequest = {
      fileName: '',
      turbines: [],
      signals: [],
      granularity: GRANULARITIES['10m'],
      queryType: this.enumQueryType.Scada,
      fileType: '',
      startTS: Number(this.dataSelectOptions.dateFrom),
      endTS: Number(this.dataSelectOptions.dateTo),
      wrongDate: false
    };
  }

  public selectAllSignals() {
    this.selectAllSignalsFlag = !this.selectAllSignalsFlag;
    this.signalGroupArray.forEach(element => {
      element.signalGroupName.checked = this.selectAllSignalsFlag;
      element.signals.forEach(signal => {
        signal.checked = this.selectAllSignalsFlag;
      });
    });
    this.checkSelectedSignals();
  }

  public selectAllSignalsByGroup(groupName) {
    this.signalGroupArray.forEach(element => {
      if (element.signalGroupName.name === groupName) {
        element.signals.forEach(signal => {
          signal.checked = element.signalGroupName.checked;
        });
      }
    });
    this.checkSelectedSignals();
  }

  public checkSelectedSignals(): void {
    this.dataSelectOptions.fileRequest.signals = [];
    let signals = 0;
    this.signalGroupArray.forEach(element => {
      element.signals.forEach(signal => {
        if (signal.checked) {
          signals++;
          if (!this.dataSelectOptions.fileRequest.signals.includes(signal.value)) {
            this.dataSelectOptions.fileRequest.signals.push(signal.value);
          }
        }
      });
      switch (element.signalGroupName.name) {
        case 'ambient':
          const ambientSignals = element.signals.filter(data => data.checked === true);
          this.ambientSignalGroupSelected = ambientSignals.length;
          element.signalGroupName.checked = element.signals.length === this.ambientSignalGroupSelected ? true : false;
          break;
        case 'electrical':
          const electricalSignals = element.signals.filter(data => data.checked === true);
          this.electricalSignalGroupSelected = electricalSignals.length;
          element.signalGroupName.checked = element.signals.length === this.electricalSignalGroupSelected ? true : false;
          break;
        case 'technical':
          const technicalSignals = element.signals.filter(data => data.checked === true);
          this.technicalSignalGroupSelected = technicalSignals.length;
          element.signalGroupName.checked = element.signals.length === this.technicalSignalGroupSelected ? true : false;
          break;
        case 'temperature':
          const temperatureSignals = element.signals.filter(data => data.checked === true);
          this.temperatureSignalGroupSelected = temperatureSignals.length;
          element.signalGroupName.checked = element.signals.length === this.temperatureSignalGroupSelected ? true : false;
          break;
      }
    });
    this.totalSignalsSelected = signals;
    this.selectAllSignalsFlag = this.totalSignalsSelected === this.totalSignals ? true : false;
    this.calculateSize();
  }

  private getAvailableTSForPark(parkName) {
    this.dataStudioService.getAvailableTS(parkName).pipe(takeUntil(this.unsubscribe$)).subscribe(data => {

      if (data) {
        this.minDate = new Date(data);
      }
    });
  }

  private setAssetNames() {
    this.assetParksNames = this.assetParks.map(asset => asset.fe_name);
  }

  private setTurbinesList() {
    this.turbinesList = this.turbineWithCodeArray.map(turbine => turbine.displayName);
  }

  private subscribeLanguageChange() {
    this.languageService.updateCalendars(this.translateService.currentLang,this.localeService);
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe(event => {
      if (event) {
        this.languageService.updateCalendars(event.lang,this.localeService);
      }
    });
    this.signalGroupArray = this.sortTranslateSignals(this.signalGroupArray);
  }

  private sortTranslateSignals(signalGroupArray){
    return signalGroupArray.map(item => ({
      ...item,
      signals: item.signals.sort((a,b) => this.sortTranslates(a, b))
    }))
  }

  private sortTranslates(a,b) {
    const translatedA = this.translateService.instant(a.name) || a.name;
    const translatedB = this.translateService.instant(b.name) || b.name;
    return translatedA.localeCompare(translatedB);
  }

}
